import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit';

import { listJizdy } from '@/api/services/jizdyService';
import type { JizdaListItem, JizdyFilters } from '@/api/types/Jizda';
import { todayIsoDate } from '@/utils/date';

import type { RootState } from './index';

// Jízdy list state (CHR-02 / ADR-0036). The flat `Jizda` is the overview row;
// the CHR-01 Order grouping is gone. Slice/file name kept (`orders`) — the
// state holds jízdy. Renaming the slice is a downstream cosmetic pass.

export type OrdersStatus = 'idle' | 'loading' | 'loadingMore' | 'ready' | 'error';

export interface OrdersState {
  items: JizdaListItem[];
  cursor: string | null;
  hasMore: boolean;
  filters: JizdyFilters;
  status: OrdersStatus;
  error: string | null;
  // When the axios interceptor fires `api:optimistic-lock` for a jízda id,
  // the editor surfaces a "Reload" affordance for that one row.
  optimisticLockedOrderId: string | null;
  // STALE-RESPONSE GUARD (BUG-1, ported from the
  // chr-04 e2e-suite-repair). The list has exactly ONE in-flight
  // query at a time — `loadOrdersPage` is the slice's only thunk and every
  // dispatch, reset or append, supersedes whatever was in flight because it
  // reads the CURRENT `filters`/`cursor`. Flipping two filters (or paging)
  // in quick succession leaves two GETs racing, and HTTP does not promise
  // ordering: a slow FIRST request can land AFTER a fast second one and
  // repaint the previous filter's rows under the new filter chips —
  // indistinguishable from fresh data. Every `pending` claims the slot;
  // `fulfilled`/`rejected` refuse to write unless the claim is still theirs.
  latestRequestId: string | null;
}

const initialState: OrdersState = {
  items: [],
  cursor: null,
  hasMore: true,
  filters: {
    from: todayIsoDate(),
    showDeleted: false,
    sortBy: 'createdAt',
    sortDir: 'asc',
  },
  status: 'idle',
  error: null,
  optimisticLockedOrderId: null,
  latestRequestId: null,
};

interface PageThunkArg {
  reset: boolean;
}

// Loads a page of jízdy. `reset=true` discards the current list (filter
// changes); `reset=false` appends (infinite scroll). Consumes
// `state.orders.filters` so the dropdowns and the thunk stay in sync.
export const loadOrdersPage = createAsyncThunk<
  { items: JizdaListItem[]; nextCursor: string | null; reset: boolean },
  PageThunkArg,
  { state: RootState; rejectValue: string }
>('orders/loadPage', async ({ reset }, { getState, rejectWithValue }) => {
  const state = getState();
  const cursor = reset ? null : state.orders.cursor;
  try {
    const page = await listJizdy(state.orders.filters, cursor);
    return { items: page.items, nextCursor: page.nextCursor, reset };
  } catch (e) {
    const message = e instanceof Error ? e.message : 'unknown';
    return rejectWithValue(message);
  }
});

const ordersSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    setFilters(state, action: PayloadAction<Partial<JizdyFilters>>) {
      state.filters = { ...state.filters, ...action.payload };
    },
    // Replaces a single jízda in-place after a save / patch round-trip.
    upsertOrder(state, action: PayloadAction<JizdaListItem>) {
      const idx = state.items.findIndex((o) => o.id === action.payload.id);
      if (idx >= 0) {
        state.items[idx] = action.payload;
      } else {
        state.items.push(action.payload);
      }
    },
    // Removes a jízda from the local list after a successful soft-delete.
    removeOrder(state, action: PayloadAction<string>) {
      state.items = state.items.filter((o) => o.id !== action.payload);
    },
    markOptimisticLock(state, action: PayloadAction<string>) {
      state.optimisticLockedOrderId = action.payload;
    },
    clearOptimisticLock(state) {
      state.optimisticLockedOrderId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadOrdersPage.pending, (state, action) => {
        state.status = action.meta.arg.reset ? 'loading' : 'loadingMore';
        state.error = null;
        state.latestRequestId = action.meta.requestId;
      })
      .addCase(loadOrdersPage.fulfilled, (state, action) => {
        if (state.latestRequestId !== action.meta.requestId) return;
        state.status = 'ready';
        state.error = null;
        state.cursor = action.payload.nextCursor;
        state.hasMore = action.payload.nextCursor !== null;
        if (action.payload.reset) {
          state.items = action.payload.items;
        } else {
          state.items.push(...action.payload.items);
        }
      })
      .addCase(loadOrdersPage.rejected, (state, action) => {
        // A superseded request that FAILS must not error-out (or blank) the
        // screen a newer request has already filled — the guard covers BOTH
        // terminal cases, not just `fulfilled`.
        if (state.latestRequestId !== action.meta.requestId) return;
        state.status = 'error';
        state.error = action.payload ?? 'unknown';
      });
  },
});

export const {
  setFilters,
  upsertOrder,
  removeOrder,
  markOptimisticLock,
  clearOptimisticLock,
} = ordersSlice.actions;

export const ordersReducer = ordersSlice.reducer;

// Selectors
export const selectOrders = (state: RootState): JizdaListItem[] => state.orders.items;
export const selectOrdersStatus = (state: RootState): OrdersStatus => state.orders.status;
export const selectOrdersError = (state: RootState): string | null => state.orders.error;
export const selectOrdersHasMore = (state: RootState): boolean => state.orders.hasMore;
export const selectOrdersFilters = (state: RootState): JizdyFilters =>
  state.orders.filters;
export const selectOptimisticLockedOrderId = (state: RootState): string | null =>
  state.orders.optimisticLockedOrderId;
